import { cn } from '../../lib/cn';

/**
 * A record's state, rendered as a quiet chip.
 *
 * Contract: `design/conventions.md` §2. Three things it exists to stop:
 *
 * 1. **The raw code reaching a person.** The customer drawer rendered `returned_collected` and
 *    `assigned` for months, inside a monospace `<code>` chip — the typeface that means "machine
 *    value, do not read this", applied to the most human field on the panel. Pass `label` from the
 *    lookup table (`atlas_lookup.booking_statuses.display_name`), never the stored code.
 * 2. **A colour per status.** Colour maps to the lifecycle PHASE, so a new status inherits a
 *    sensible treatment instead of needing a design decision, and so the palette stays small.
 * 3. **Hex outside the token package.** `booking_statuses` also stores a `color_hex`; using it
 *    would break rule 1 of the design contract. The phase maps to a semantic token here instead.
 *
 * NOT A PILL. `radius-sm`, not `rounded-full` — a pill reads as a tag or a filter, something you
 * can click or remove. This is a fact about the record.
 */

/**
 * Lifecycle phases, as stored in `atlas_lookup.booking_statuses.lifecycle_phase` and the
 * equivalent lookups elsewhere. `unknown` covers an unmapped code so the component can never throw
 * on data it has not seen.
 */
export type StatusPhase =
  | 'pre_rental' | 'rental_active' | 'post_rental' | 'cancelled' | 'exception' | 'unknown';

/**
 * Phase → treatment.
 *
 * THE ONE WORTH ARGUING ABOUT: `post_rental` is NEUTRAL, not green. A finished rental is not a
 * success, it is *over*. Most tickets on most days concern finished rentals, so painting that
 * state green turns the whole surface green and green stops carrying meaning. Neutral here is what
 * keeps `success` meaning "live right now" and `danger` meaning "somebody must look at this".
 */
const PHASE_CLASS: Record<StatusPhase, string> = {
  pre_rental: 'bg-accent-100 text-accent-700 dark:bg-accent-800 dark:text-accent-100',
  rental_active: 'bg-success-soft text-success-text',
  post_rental: 'bg-bg-inset text-text-secondary',
  // An absence, not a state. No fill at all, and no left padding, so it reads as plain text.
  cancelled: 'text-text-muted',
  exception: 'bg-danger-soft text-danger-text',
  unknown: 'bg-bg-inset text-text-secondary',
};

/**
 * Last-resort label for a code with no row in the lookup.
 *
 * Deliberately NOT `.replace(/_/g, ' ')` on its own — that yields "Returned Collected" where the
 * curated label is "Returned / Collected". This is a fallback that should look slightly wrong, so
 * that a missing lookup row gets noticed and fixed in the data rather than papered over here.
 */
export function humaniseStatusCode(code: string): string {
  return code
    .split('_')
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

export interface StatusChipProps {
  /** The curated label. Prefer the lookup's `display_name`. */
  label?: string | null;
  /** The stored code. Used only to derive a fallback label, and for the title attribute. */
  code?: string | null;
  /** Lifecycle phase from the same lookup row. */
  phase?: StatusPhase | string | null;
  className?: string;
}

export function StatusChip({ label, code, phase, className }: StatusChipProps) {
  const text = label?.trim() || (code ? humaniseStatusCode(code) : null);
  if (!text) return null;

  const key: StatusPhase = (phase && phase in PHASE_CLASS ? phase : 'unknown') as StatusPhase;

  return (
    <span
      className={cn(
        'inline-flex items-center rounded-sm px-1.5 text-caption font-medium leading-5',
        /*
         * NO `px-0` FOR CANCELLED. It existed because the reference writes
         * `.st.cancelled{padding-left:0}` — but the reference has no negative margin, and Atlas's
         * Row cancels the chip's `px-1.5` with `-ml-1.5` so the chip's TEXT lands on the value
         * column. With the padding already zero that margin pulled cancelled statuses 6px left of
         * every other value on the card. The cancelled ground is transparent, so uniform padding
         * is invisible and the alignment holds for every phase.
         */
        PHASE_CLASS[key],
        className,
      )}
      /*
       * The stored code stays reachable on hover when it differs from the label. An agent never
       * needs it; someone debugging a sync does, and this saves them opening the database. Omitted
       * when the label IS the code, which would just be noise.
       */
      title={code && code !== text ? code : undefined}
    >
      {text}
    </span>
  );
}
